import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import API_BASE_URL from 'src/constants';

export type PromotionType = 'black_week' | 'sale' | 'launch' | 'campaign' | 'season' | 'other';

export type PromotionState = 'draft' | 'submitted' | 'approved' | 'rejected' | 'active' | 'completed';

export type PromotionSyncState = 'pending' | 'success' | 'failed';

export type PromotionChangeRequestType = 'edit' | 'delete';

export type PromotionChangeRequestState = 'draft' | 'submitted' | 'approved' | 'rejected';

export interface CustomerForecastPromotion {
  id: number;
  partner_id: number;
  partner_name: string;
  name: string;
  type: PromotionType;
  date_from: string; // ISO date
  date_to: string; // ISO date
  multiplier?: number | null;
  additional_orders?: number | null;
  description?: string | null;
  state: PromotionState;
  rejection_reason?: string | null;
  submitted_at?: string | null;
  approved_at?: string | null;
  rejected_at?: string | null;
  pending_change?: PromotionChangeRequestType | null;
  last_sync_state?: PromotionSyncState | null;
  last_sync_error?: string | null;
}

export interface CustomerForecastPromotionWriteIn {
  name: string;
  type: PromotionType;
  date_from: string;
  date_to: string;
  multiplier?: number | null;
  additional_orders?: number | null;
  description?: string | null;
}

export interface CustomerForecastPromotionChangeRequestCreate {
  request_type: PromotionChangeRequestType;
  reason?: string | null;
  changes?: CustomerForecastPromotionWriteIn | null;
}

export interface CustomerForecastPromotionChangeRequest {
  id: number;
  promotion_id: number;
  request_type: PromotionChangeRequestType;
  reason?: string | null;
  state: PromotionChangeRequestState;
  submitted_at?: string | null;
  decided_at?: string | null;
  decision_reason?: string | null;
}

const PORTAL = `${API_BASE_URL}portal`;

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

async function asJson<T>(res: Response): Promise<T> {
  if (!res.ok) {
    const body = await res.text().catch(() => '');
    throw new Error(`HTTP ${res.status}: ${body || res.statusText}`);
  }
  return (await res.json()) as T;
}

export const promotionsApi = {
  listOwn: () =>
    fetch(`${PORTAL}/customer-forecast-promotions`, { headers: authHeaders() }).then(
      asJson<CustomerForecastPromotion[]>,
    ),

  create: (payload: CustomerForecastPromotionWriteIn) =>
    fetch(`${PORTAL}/customer-forecast-promotions`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecastPromotion>),

  update: (id: number, payload: CustomerForecastPromotionWriteIn) =>
    fetch(`${PORTAL}/customer-forecast-promotions/${id}`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecastPromotion>),

  delete: (id: number) =>
    fetch(`${PORTAL}/customer-forecast-promotions/${id}`, {
      method: 'DELETE',
      headers: authHeaders(),
    }).then(asJson<{ ok: boolean }>),

  submit: (id: number) =>
    fetch(`${PORTAL}/customer-forecast-promotions/${id}/submit`, {
      method: 'POST',
      headers: authHeaders(),
    }).then(asJson<CustomerForecastPromotion>),

  requestChange: (id: number, payload: CustomerForecastPromotionChangeRequestCreate) =>
    fetch(`${PORTAL}/customer-forecast-promotions/${id}/change-request`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    }).then(asJson<CustomerForecastPromotionChangeRequest>),
};

const QK = {
  list: ['forecastPromotions', 'list'] as const,
};

export const useOwnPromotions = () =>
  useQuery({
    queryKey: QK.list,
    queryFn: promotionsApi.listOwn,
  });

export const useCreatePromotion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CustomerForecastPromotionWriteIn) => promotionsApi.create(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecastPromotions'] }),
  });
};

export const useUpdatePromotion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CustomerForecastPromotionWriteIn }) =>
      promotionsApi.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecastPromotions'] }),
  });
};

export const useDeletePromotion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => promotionsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecastPromotions'] }),
  });
};

export const useSubmitPromotion = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => promotionsApi.submit(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecastPromotions'] }),
  });
};

export const useRequestPromotionChange = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CustomerForecastPromotionChangeRequestCreate }) =>
      promotionsApi.requestChange(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecastPromotions'] }),
  });
};

export const PROMOTION_TYPE_LABEL_KEY: Record<PromotionType, string> = {
  black_week: 'forecastPromotions.type.black_week',
  sale: 'forecastPromotions.type.sale',
  launch: 'forecastPromotions.type.launch',
  campaign: 'forecastPromotions.type.campaign',
  season: 'forecastPromotions.type.season',
  other: 'forecastPromotions.type.other',
};

export const PROMOTION_STATE_LABEL_KEY: Record<PromotionState, string> = {
  draft: 'forecastPromotions.state.draft',
  submitted: 'forecastPromotions.state.submitted',
  approved: 'forecastPromotions.state.approved',
  rejected: 'forecastPromotions.state.rejected',
  active: 'forecastPromotions.state.active',
  completed: 'forecastPromotions.state.completed',
};
